import { FetchData } from 'use-http';
import { IUser, UserFilter } from 'types/users';
import { deviceDetect } from 'react-device-detect';
import { ICoordinate } from 'models/user';

const SERVICE_TYPE_TITLE_MAP: Record<string, string> = {
	Аутсорс: 'ИСИТ',
	аутсорс: 'ИСИТ',
	Уборщица: 'Уборка',
	Грузчик: 'Разгрузка',
	'Продавец-аутсорсер': 'Обслуживание зал',
	'Продавец-аутсорс': 'Обслуживание зал',
	'Кассир-аутсорсер': 'Обслуживание покупателей',
	'Кассир-аутсорс': 'Обслуживание покупателей',
};

/**
 * Форматирует ФИО пользователя в строку
 * @param user - объект пользователя с полями firstname, secondname, middlename
 * @returns отформатированное ФИО или 'Без ФИО' если все поля пустые
 */
export const formatUserFullName = (user?: { 
	firstname?: string; 
	secondname?: string; 
	middlename?: string;
} | null): string => {
	if (!user) return 'Без ФИО';
	
	const fullName = `${user.secondname || ''} ${user.firstname || ''} ${user.middlename || ''}`.trim();
	return fullName || 'Без ФИО';
};

export const formatServiceTypeTitle = (title?: string | null): string => {
	if (!title) return '';
	return SERVICE_TYPE_TITLE_MAP[title] || title;
};

export const downloadFile = async <TData>(post: FetchData<TData>, files: FileList) => {
	if (files instanceof FileList) {
		const data = new FormData();
		data.append('file', files[0]);
		const result = await post('api/file/download', data);
		return result;
	}
	return files;
};

export const extractManagers = (stores: any[]) => {
	const aoManagersSet = new Set<string>();
	const regionalManagersSet = new Set<string>();

	for (const row of stores) {
		if (row.regional_manager && !['Вакансия', 'Неизвестно'].includes(row.regional_manager)) {
			regionalManagersSet.add(row.regional_manager);
		}
		if (row.ao_manager && !['Вакансия', 'Неизвестно'].includes(row.ao_manager)) {
			aoManagersSet.add(row.ao_manager);
		}
	}

	const aoManagers = Array.from(aoManagersSet).map(value => ({ value, label: value }));
	const regionalManagers = Array.from(regionalManagersSet).map(value => ({ value, label: value }));

	return { aoManagers, regionalManagers };
};

export const convertUserFilter = (data: UserFilter[]) => {
	if (!Array.isArray(data)) {
		console.error('Expected an array, but got:', data);
		return [];
	}
	
	// Убираем дубликаты по userId
	const uniqueUsers = Array.from(
		new Map(
			data.map(item => [
				item.userId,
				{
					value: item.userId,
					label: formatUserFullName(item),
				},
			])
		).values()
	);
	
	return uniqueUsers;
};

export const convertUser = (data: IUser[]) => {
	return data.map((item: IUser) => {
		return {
			value: String(item.id),
			label: `${item.firstname ? item.firstname : ''} ${item.secondname ? item.secondname : ''} ${
				item.middlename ? item.middlename : ''
			}`,
		};
	});
};

export const tokenDecode = (token: string | null) => {
	if (token) {
		const base64Url = token.split('.')[1];
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map(function (c) {
					return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
				})
				.join(''),
		);

		// Парсим полученный JSON-объект
		const payload = JSON.parse(jsonPayload);
		return payload;
	}
	return null;
};

export const dataURItoBlob = (dataURI: string) => {
	let byteString;
	if (dataURI.split(',')[0].indexOf('base64') >= 0) byteString = atob(dataURI.split(',')[1]);
	else byteString = unescape(dataURI.split(',')[1]);

	const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

	const ia = new Uint8Array(byteString.length);
	for (let i = 0; i < byteString.length; i += 1) {
		ia[i] = byteString.charCodeAt(i);
	}

	return new Blob([ia], { type: mimeString });
};

interface CheckinResponse {
	statusCode: number;
	message?: string;
	id?: string;
	error?: string;
}

const IDEMPOTENCY_COOKIE_NAME = 'checkin_idempotency_key';
const IDEMPOTENCY_EXPIRY_MINUTES = 5;

const generateIdempotencyKey = (): string => {
	return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const getIdempotencyKey = (): string | null => {
	return (
		document.cookie
			.split('; ')
			.find(row => row.startsWith(IDEMPOTENCY_COOKIE_NAME))
			?.split('=')[1] || null
	);
};

const setIdempotencyKey = (key: string): void => {
	const expiryDate = new Date();
	expiryDate.setMinutes(expiryDate.getMinutes() + IDEMPOTENCY_EXPIRY_MINUTES);
	document.cookie = `${IDEMPOTENCY_COOKIE_NAME}=${key}; expires=${expiryDate.toUTCString()}; path=/`;
};

export const registrCheckin = async <T extends CheckinResponse>(
	post: FetchData<T>,
	code?: string,
	token?: string,
	coordinate?: ICoordinate,
	fileId?: number,
): Promise<T> => {
	// Получаем существующий ключ идемпотентности или генерируем новый
	let idempotencyKey = getIdempotencyKey();
	if (!idempotencyKey) {
		idempotencyKey = generateIdempotencyKey();
		setIdempotencyKey(idempotencyKey);
	}

	const checkinRes = await post('/api/checkin/registr', {
		code,
		token,
		userAgent: deviceDetect(window.navigator.userAgent),
		lat: coordinate?.latitude,
		lon: coordinate?.longitude,
		fileId,
		datetimeStr: `${new Date()}`,
		timezoneOffset: new Date().getTimezoneOffset(),
		idempotencyKey,
	});

	if (checkinRes?.statusCode) {
		throw new Error(checkinRes.message || 'Ошибка при регистрации в ЭЖ');
	}

	return checkinRes;
};

export const registrCheckinAnother = async <T extends CheckinResponse>(
	post: FetchData<T>,
	code: string,
	userId: string,
	fileId: number,
	coordinate?: ICoordinate,
): Promise<T> => {
	const currentDate = new Date();
	const checkin = await post('/api/checkin/registrAnother', {
		code,
		userId,
		fileId,
		type: 'manual',
		datetimeStr: `${currentDate}`,
		timezoneOffset: currentDate.getTimezoneOffset(),
		lat: coordinate?.latitude,
		lon: coordinate?.longitude,
	});

	if (checkin?.statusCode) {
		throw new Error(checkin.message || 'Ошибка при регистрации в ЭЖ');
	}

	return checkin;
};

export const isNoCoords = () => !/Mobi|Android/i.test(navigator.userAgent) || /DT40/i.test(navigator.userAgent);
